import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { installMonitorQuery, type MonitorPackage, type MonitorProblem } from '../api/softsee'
import { moduleOf, shellQuery } from '../api/shell'
import { SelectPicker } from '../ui/SelectPicker'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * Softsee::installMonitor - cum au mers instalarile programate pe tot parcul.
 *
 * Ecranul e asezat invers fata de un tabel obisnuit: sus cifrele, apoi pachetele
 * si sediile - ca sa se vada dintr-o privire care e cel cu probleme - iar tabelul
 * cu statiile pe care n-a mers e ultimul si cel mai lung, fiindca el e cel din
 * care se lucreaza. Din el lipseau tocmai partile de la mijloc: pagina arata
 * cifrele de sus si sarea direct la o lista de trei coloane.
 *
 * Culorile nu sunt podoaba, sunt aceleasi din discovery: rosu e mereu esec,
 * galben e ceva care asteapta un om, verde e ce a mers, albastru e ce se
 * intampla acum.
 */

/* Benzile, cardurile si cifrele vin din adm.css - ecranul vechi o aducea prin
   `loadPlugins = ['adm']`. Fara ea, pagina e o lista pe fundal alb. */
const STYLES = ['css/adm.css']

/* Starile unei rulari - aceleasi numere ca Install_jobs. */
const RUN_SENT = '1'
const RUN_TAKEN = '2'
const RUN_DONE = '3'
const RUN_FAILED = '4'
const RUN_EXPIRED = '5'

const PERIODS = [7, 30, 90, 365]

/**
 * Filtrele din adresa, ca `apply()` din monitor.php: `?days=90&offices[]=3`.
 * Asa un ecran cu "Cluj, ultimele 90 de zile" se trimite cuiva si se deschide
 * la fel la el. Perioada necunoscuta cade pe 30, ca `?: 30` din controller.
 */
export function filtersFromUrl(search: string): { days: number; offices: number[] } {
  const q = new URLSearchParams(search)
  const days = Number(q.get('days'))
  const offices = [...q.getAll('offices[]'), ...q.getAll('offices')].map(Number).filter((n) => Number.isInteger(n) && n > 0)
  return { days: PERIODS.includes(days) ? days : 30, offices: [...new Set(offices)] }
}

function filtersToUrl(days: number, offices: number[]): string {
  const q = new URLSearchParams()
  q.set('days', String(days))
  offices.forEach((id) => q.append('offices[]', String(id)))
  return '?' + q.toString()
}

export function InstallMonitorPage() {
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const [initial] = useState(() => filtersFromUrl(typeof window === 'undefined' ? '' : window.location.search))
  const [days, setDays] = useState(initial.days)
  const [offices, setOffices] = useState<number[]>(initial.offices)
  /* Filtrul nou nu mai sterge pagina cu un spinner: cifrele vechi raman pana
     vin cele noi. */
  const query = useQuery({ ...installMonitorQuery(offices, days), placeholderData: keepPreviousData })

  /* Adresa urmeaza filtrele. `replaceState` cu starea istoriei neatinsa: nu se
     face o intrare noua in Back pentru fiecare sediu bifat, iar routerul isi
     pastreaza cheia intrarii. */
  useEffect(() => {
    if (typeof window === 'undefined') return
    const next = filtersToUrl(days, offices)
    if (window.location.search === next) return
    window.history.replaceState(window.history.state, '', window.location.pathname + next + window.location.hash)
  }, [days, offices])

  if (query.isPending) {
    return (
      <Content>
        <Spinner />
      </Content>
    )
  }
  if (query.isError) {
    return (
      <Content>
        <div className="alert alert-danger">{(query.error as Error).message}</div>
      </Content>
    )
  }

  const page = query.data
  const totals = page.totals
  const done = totals[RUN_DONE] ?? 0
  const failed = totals[RUN_FAILED] ?? 0
  const expired = totals[RUN_EXPIRED] ?? 0
  const waiting = (totals[RUN_SENT] ?? 0) + (totals[RUN_TAKEN] ?? 0)
  const all = Object.values(totals).reduce((a, b) => a + b, 0)
  /* Cifra din antet e "cate n-au ajuns", nu "cate au fost": cine deschide
     ecranul intreaba daca are treaba. */
  const missing = failed + expired

  return (
    <Content>
      <style>{MONITOR_CSS}</style>
      <div id="content">
        <div className="adm-page-head adm-tone-blue-mauve">
          <span className="adm-page-icon">
            <i className="fas fa-box-open"></i>
          </span>
          <div className="adm-page-title">
            <div className="adm-page-title-line">
              <h3>Scheduled installs</h3>
            </div>
            <span className="adm-page-note">whether the packages landed everywhere they were sent, and where they did not</span>
          </div>
          <div className="adm-page-stats">
            <HeadStat value={all} label="sent" tone="grey" />
            <HeadStat value={missing} label="did not land" tone={missing > 0 ? 'red-intense' : 'green-mode'} />
            <HeadStat value={waiting} label="waiting" tone="blue-hoki" />
          </div>
        </div>

        <div className="adm-bar">
          <div className="adm-field">
            <label>Offices</label>
            {/* Sertarul consolei, ca in monitor.php: cautare, Select All /
                Deselect All, "All offices" cand nu e ales nimic. */}
            <div style={{ width: 280 }}>
              <SelectPicker
                id="imOffices"
                title="All offices"
                multiple
                actionsBox
                selectedTextFormat="count > 2"
                size={10}
                value={offices.map(String)}
                onChange={(values) => setOffices(values.map(Number))}
                options={page.offices.map((o) => ({ value: String(o.ID), label: o.Name }))}
              />
            </div>
          </div>
          <div className="adm-field">
            <label>Period</label>
            <select
              className="form-control"
              id="imDays"
              style={{ width: 160 }}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>

        {all < 1 ? (
          <div className="adm-card adm-tone-grey">
            <div className="adm-empty">
              <i className="fas fa-inbox"></i>
              <p>Nothing has been sent in this period.</p>
              {/* Doua motive diferite arata identic aici. */}
              <span>Either nothing was scheduled, or the chosen offices hold no devices.</span>
            </div>
          </div>
        ) : (
          <>
            <div className="row adm-tiles">
              <Tile tone="green-mode" icon="fa-check-circle" value={done} label="Installed" />
              <Tile tone="red-intense" icon="fa-times-circle" value={failed} label="Failed" />
              <Tile tone="yellow" icon="fa-hourglass-end" value={expired} label="Expired" />
              <Tile tone="blue-madison" icon="fa-clock" value={waiting} label="Waiting" />
            </div>

            <div className="adm-section adm-section-green-mode">
              <h4>
                <i className="fas fa-cubes"></i> By package
              </h4>
              {/* Pe pachet, nu pe programare: "a intrat Office pe toate statiile"
                  nu se uita la cate programari s-au facut pentru el. */}
              <span className="adm-section-note">on how many devices each package landed, and on how many it did not</span>
            </div>
            <div className="adm-card adm-tone-green-mode">
              <table className="table adm-list" width="100%">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th style={{ width: 90 }}>Devices</th>
                    <th style={{ width: 190 }}>Installed</th>
                    <th style={{ width: 80 }}>Failed</th>
                    <th style={{ width: 80 }}>Expired</th>
                    <th style={{ width: 80 }}>Waiting</th>
                    <th style={{ width: 140 }}>Last sent</th>
                  </tr>
                </thead>
                <tbody>
                  {page.packages.map((pack, i) => (
                    <PackageRow key={i} pack={pack} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="adm-section adm-section-blue-hoki">
              <h4>
                <i className="fas fa-building"></i> By office
              </h4>
              <span className="adm-section-note">which site is the one with the trouble</span>
            </div>
            <div className="adm-card adm-tone-blue-hoki">
              <table className="table adm-list" width="100%">
                <thead>
                  <tr>
                    <th>Office</th>
                    <th style={{ width: 90 }}>Devices</th>
                    <th style={{ width: 90 }}>Installed</th>
                    <th style={{ width: 80 }}>Failed</th>
                    <th style={{ width: 80 }}>Expired</th>
                    <th style={{ width: 80 }}>Waiting</th>
                  </tr>
                </thead>
                <tbody>
                  {page.byOffice.map((office, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{office.office_name}</strong>
                      </td>
                      <td>{office.devices}</td>
                      <td>{office.done}</td>
                      <td>
                        <Count value={office.failed} tone="bad" />
                      </td>
                      <td>
                        <Count value={office.expired} tone="warn" />
                      </td>
                      <td>{office.pending > 0 ? office.pending : <span className="adm-muted">0</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="adm-section adm-section-red-intense">
              <h4>
                <i className="fas fa-exclamation-triangle"></i> Where it did not land
              </h4>
              {/* Picate si expirate la un loc - amandoua inseamna "pachetul nu e
                  pe masina aia" - dar cu starea alaturi, fiindca se repara altfel. */}
              <span className="adm-section-note">failed and expired together &mdash; both mean the package is not on that machine</span>
            </div>
            <div className="adm-card adm-tone-red-intense">
              {page.problems.length === 0 ? (
                <div className="adm-empty">
                  <i className="fas fa-check-circle" style={{ color: '#5FB644' }}></i>
                  <p>Every package landed everywhere it was sent.</p>
                </div>
              ) : (
                <table className="table adm-list" width="100%">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th style={{ width: 170 }}>Office</th>
                      <th>Package</th>
                      <th style={{ width: 110 }}>State</th>
                      <th style={{ width: 140 }}>Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.problems.map((problem) => (
                      <ProblemRow key={problem.ID} problem={problem} states={page.states} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </Content>
  )
}

/*
 * Cifra din antet, exact ca in `softsee/adm/_pagehead.php`.
 *
 * NU e `dashboard-stat`: aia e caseta platformei, desenata pentru un sfert de
 * rand (`display:block`, `min-height:135px`), si pusa in antet fara `col-*` in
 * jur se intinde pe toata latimea - trei carduri unul sub altul. Antetul are
 * casetele lui, `adm-stat`, pe care `.adm-page-stats` le aseaza in rand.
 */
function HeadStat({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div className={`adm-stat adm-stat-${tone}`}>
      <span className="adm-stat-value">{value}</span>
      <span className="adm-stat-label">{label}</span>
    </div>
  )
}

function Tile({ tone, icon, value, label }: { tone: string; icon: string; value: number; label: string }) {
  return (
    <div className="col-md-3 col-sm-6">
      <div className={`dashboard-stat ${tone}`}>
        <div className="visual">
          <i className={`fas ${icon}`}></i>
        </div>
        <div className="details">
          <div className="number">{value}</div>
          <div className="desc">{label}</div>
        </div>
      </div>
    </div>
  )
}

function Count({ value, tone }: { value: number; tone: 'bad' | 'warn' }) {
  if (value < 1) return <span className="adm-muted">0</span>
  return <span className={`im-pill im-pill-${tone}`}>{value}</span>
}

function PackageRow({ pack }: { pack: MonitorPackage }) {
  /*
   * Procentul se socoteste din cele care s-au SFARSIT, nu din toate: altfel un
   * pachet trimis acum, cu totul in asteptare, ar arata zero la suta si ar parea
   * o problema tocmai cand totul merge bine.
   */
  const settled = pack.done + pack.failed + pack.expired
  const rate = settled > 0 ? Math.round((pack.done * 100) / settled) : null

  return (
    <tr>
      <td>
        <strong>{pack.package_name}</strong>
      </td>
      <td>{pack.devices}</td>
      <td>
        {rate === null ? (
          <span className="adm-muted">nothing finished yet</span>
        ) : (
          <>
            <div className="im-bar">
              <i className={rate >= 90 ? 'im-ok' : rate >= 60 ? 'im-warn' : 'im-bad'} style={{ width: `${rate}%` }}></i>
            </div>
            <span className="im-rate">{rate}%</span> <span className="adm-muted">({pack.done})</span>
          </>
        )}
      </td>
      <td>
        <Count value={pack.failed} tone="bad" />
      </td>
      <td>
        <Count value={pack.expired} tone="warn" />
      </td>
      <td>{pack.pending > 0 ? pack.pending : <span className="adm-muted">0</span>}</td>
      <td className="adm-muted">{String(pack.last_due ?? '').slice(0, 16)}</td>
    </tr>
  )
}

function ProblemRow({ problem, states }: { problem: MonitorProblem; states: Record<string, { label: string; note: string }> }) {
  const state = states[String(problem.state)] ?? { label: '?', note: '' }

  return (
    <tr>
      <td>
        <a href={`/app/softsee/device/${problem.device_id}`}>{problem.device_name}</a>
      </td>
      <td className="adm-muted">{problem.office_name || '—'}</td>
      <td>{problem.package_name}</td>
      <td>
        <span
          className={`im-pill ${Number(problem.state) === Number(RUN_FAILED) ? 'im-pill-bad' : 'im-pill-warn'}`}
          data-toggle="tooltip"
          data-placement="top"
          title={state.note}
        >
          {state.label}
        </span>
      </td>
      <td className="adm-muted">{String(problem.due_at ?? '').slice(0, 16)}</td>
    </tr>
  )
}

/* Doar ce nu exista in adm.css: bara si pastilele. Restul - benzi, carduri,
   cifre, gol - vine de acolo, ca sa nu ajunga doua dashboard-uri in aceeasi
   platforma cu doua feluri de a arata la fel. */
const MONITOR_CSS = `
    .im-bar { display:inline-block; width:90px; height:8px; border-radius:4px; background:#eef1f4; overflow:hidden; vertical-align:middle; }
    .im-bar i { display:block; height:100%; }
    .im-bar i.im-ok { background:#5FB644; }
    .im-bar i.im-warn { background:#f0ad4e; }
    .im-bar i.im-bad { background:#d64635; }
    .im-rate { font-weight:600; margin-left:6px; }
    .im-pill { display:inline-block; min-width:22px; padding:1px 8px; border-radius:10px; font-size:11px; font-weight:600; color:#fff; text-align:center; }
    .im-pill-bad { background:#d64635; }
    .im-pill-warn { background:#f0ad4e; }
`
